import _ from "underscore";

import type {Result, ValidCacheData} from "@khanacademy/wonder-blocks-data";

import type {DisableAIGuide} from "./__graphql-types__/DisableAIGuide.ts";
import type {EnableAIGuide} from "./__graphql-types__/EnableAIGuide.ts";

type KAID = string;

// We use sets to make tests easier with respect to ordering.
type Operation = {
    enableParent?: KAID;
    disableParent?: KAID;
    disableChildren: Set<KAID>;
    enableChildren: Set<KAID>;
};

// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set#implementing_basic_set_operations
// ka:shareable
function difference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
    const result = new Set(setA);
    for (const elem of setB) {
        result.delete(elem);
    }
    return result;
}

function enabled([on, kaid]: [any, any]): boolean {
    return on;
}

function disabled([on, kaid]: [any, any]): boolean {
    return !on;
}

function getKaid([on, kaid]: [any, any]): KAID {
    return kaid;
}

export function categorize(
    guideEnabledForParent: boolean,
    lastSavedGuideEnabledForParent: boolean,
    guideEnabledForChild: Array<boolean>,
    lastSavedGuideEnabledForChild: Array<boolean>,
    parentKaid: KAID,
    managedChildKaids: Array<KAID>,
): Operation {
    const previouslyEnabledChildren = _.zip(
        lastSavedGuideEnabledForChild,
        managedChildKaids,
    );
    const currentlyEnabledChildren = _.zip(
        guideEnabledForChild,
        managedChildKaids,
    );

    const areEnabledChildren = new Set(
        currentlyEnabledChildren.filter(enabled).map(getKaid),
    );
    const wereEnabledChildren = new Set(
        previouslyEnabledChildren.filter(enabled).map(getKaid),
    );
    const areDisabledChildren = new Set(
        currentlyEnabledChildren.filter(disabled).map(getKaid),
    );
    const wereDisabledChildren = new Set(
        previouslyEnabledChildren.filter(disabled).map(getKaid),
    );

    if (guideEnabledForParent) {
        const enableChildren = difference(
            areEnabledChildren,
            wereEnabledChildren,
        );
        const disableChildren = difference(
            areDisabledChildren,
            wereDisabledChildren,
        );
        if (lastSavedGuideEnabledForParent) {
            return {
                enableChildren: enableChildren,
                disableChildren: disableChildren,
            };
        }
        return {
            enableChildren: enableChildren,
            enableParent: parentKaid,
            disableChildren: disableChildren,
        };
    }

    if (!guideEnabledForParent && lastSavedGuideEnabledForParent) {
        // turning off
        return {
            disableChildren: wereEnabledChildren,
            disableParent: parentKaid,
            enableChildren: new Set(),
        };
    }

    // This shouldn't happen in practice since we disable save button if no
    // change, but the helper function itself should account for it.
    // All KAIDs unchanged
    return {
        disableChildren: new Set(),
        enableChildren: new Set(),
    };
}

// ka:shareable
function isError<T extends ValidCacheData>(result: Result<T>): boolean {
    return result.status === "error";
}

// ka:shareable
function isAborted<T extends ValidCacheData>(result: Result<T>): boolean {
    return result.status === "aborted";
}

// ka:shareable
function isLoading<T extends ValidCacheData>(result: Result<T>): boolean {
    return result.status === "loading" || result.status === "no-data";
}

// ka:shareable
function isSuccess<T extends ValidCacheData>(result: Result<T>): boolean {
    return result.status === "success";
}

function checkEnableAIGuideResult(
    results: Array<Result<EnableAIGuide>>,
): Error | null | undefined {
    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ enableAIGuide: EnableAIGuide_enableAIGuide | null | undefined; }>'.
    if (results.some((r) => r.data?.enableAIGuide?.error)) {
        const errorCodes = {
            UNAUTHORIZED: 0,
            INTERNAL: 0,
            NOT_FOUND: 0,
        } as const;
        for (const result of results) {
            // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ enableAIGuide: EnableAIGuide_enableAIGuide | null | undefined; }>'.
            if (result.data?.enableAIGuide?.error?.code) {
                // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ enableAIGuide: EnableAIGuide_enableAIGuide | null | undefined; }>'.
                errorCodes[result.data?.enableAIGuide.error.code] += 1;
            }
        }
        return new Error(
            `The following errors occurred. INTERNAL: ${errorCodes.INTERNAL}, UNAUTHORIZED: ${errorCodes.UNAUTHORIZED}, NOT_FOUND: ${errorCodes.NOT_FOUND}`,
        );
    }
    return null;
}

function checkDisableAIGuideResult(
    results: Array<Result<DisableAIGuide>>,
): Error | null | undefined {
    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ disableAIGuide: DisableAIGuide_disableAIGuide | null | undefined; }>'.
    if (results.some((r) => r.data?.disableAIGuide?.error)) {
        const errorCodes = {
            UNAUTHORIZED: 0,
            INTERNAL: 0,
            NOT_FOUND: 0,
        } as const;
        for (const result of results) {
            // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ disableAIGuide: DisableAIGuide_disableAIGuide | null | undefined; }>'.
            if (result.data?.disableAIGuide?.error?.code) {
                // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ disableAIGuide: DisableAIGuide_disableAIGuide | null | undefined; }>'.
                errorCodes[result.data?.disableAIGuide.error.code] += 1;
            }
        }
        return new Error(
            `The following errors occurred. INTERNAL: ${errorCodes.INTERNAL}, UNAUTHORIZED: ${errorCodes.UNAUTHORIZED}, NOT_FOUND: ${errorCodes.NOT_FOUND}`,
        );
    }
    return null;
}

// This function flattens an array of Result<T> objects down to a single result
// which is only successful if all the the results are. There is a natural
// precedence where any error is the strongest, followed by abort, then loading.
// Finally success in only returned if every result is successful *and* the
// checkSuccessCase returns no error (since often our GraphQL results return
// an error as part of the success data).
// ka:shareable
export function combineResults<T extends ValidCacheData>(
    checkSuccessCase: (arg1: Array<Result<T>>) => Error | null | undefined,
    results: Array<Result<T>>,
): Result<number> {
    if (results.length === 0) {
        return {status: "success", data: 0};
    }

    if (results.some(isError)) {
        return {
            status: "error",
            error: new Error(
                `${
                    results.filter((r) => r.status === "error").length
                } errors occurred.`,
            ),
        };
    }
    if (results.some(isAborted)) {
        return {
            status: "aborted",
        };
    }
    if (results.some(isLoading)) {
        return {
            status: "loading",
        };
    }
    if (results.every(isSuccess)) {
        const err = checkSuccessCase(results);
        if (err) {
            return {
                status: "error",
                error: err,
            };
        }
        return {
            status: "success",
            data: results.length, // return number of results
        };
    }

    return {status: "error", error: new Error("Unreachable state")};
}

export const combineEnableAIGuideResults = (
    results: Array<Result<EnableAIGuide>>,
): Result<number> => combineResults(checkEnableAIGuideResult, results);

export const combineDisableAIGuideResults = (
    results: Array<Result<DisableAIGuide>>,
): Result<number> => combineResults(checkDisableAIGuideResult, results);

// ka:shareable
export function setIndex<T>(
    value: T,
    index: number,
    array: Array<T>,
): Array<T> {
    return array.map((v, i) => (i === index ? value : v));
}
